import { useMemo, type JSX } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import type { MakerWorkOrderListItem } from '@/types/logsheets-maker';

/**
 * Phase 24 (v1.4) — BulkWorkOrderStep
 *
 * Step 2 of the bulk-create wizard. Filters the maker's WO list by the
 * client picked in step 1 and renders one tappable Card per WO showing
 * the WO number, the date range, and a machine-count badge.
 *
 * Mobile-first: full-width cards, tap targets ≥ 44 px, single column.
 */

export interface BulkWorkOrderStepProps {
  readonly workOrders: readonly MakerWorkOrderListItem[];
  readonly clientName: string;
  readonly onSelectWorkOrder: (wo: MakerWorkOrderListItem) => void;
  readonly selectedWorkOrderId?: number | null;
  readonly onBack: () => void;
}

function woClientName(wo: MakerWorkOrderListItem): string {
  // eslint-disable-next-line @typescript-eslint/no-deprecated -- legacy `client` field fallback per Plan 22-03 D-227
  return wo.clientName ?? wo.client ?? 'Unknown';
}

export function BulkWorkOrderStep({
  workOrders,
  clientName,
  onSelectWorkOrder,
  selectedWorkOrderId,
  onBack,
}: BulkWorkOrderStepProps): JSX.Element {
  const filtered = useMemo(
    () => workOrders.filter((wo) => woClientName(wo) === clientName),
    [workOrders, clientName],
  );

  return (
    <div className="space-y-4" data-testid="bulk-step-wo">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-sm text-muted-foreground">Client</p>
          <p className="font-medium">{clientName}</p>
        </div>
        <Button
          type="button"
          variant="ghost"
          onClick={onBack}
          data-testid="bulk-wo-back"
        >
          Back
        </Button>
      </div>

      {filtered.length === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="bulk-wo-empty">
          No work orders for this client.
        </p>
      )}

      <div className="space-y-3">
        {filtered.map((wo) => {
          const isSelected = selectedWorkOrderId === wo.id;
          const machineCount = wo.machines.length;
          return (
            <Card
              key={wo.id}
              className={
                isSelected
                  ? 'border-primary ring-2 ring-primary cursor-pointer'
                  : 'cursor-pointer hover:bg-muted/40'
              }
              data-testid={`bulk-wo-card-${String(wo.id)}`}
              onClick={() => {
                onSelectWorkOrder(wo);
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelectWorkOrder(wo);
                }
              }}
              style={{ minHeight: '44px' }}
            >
              <CardContent className="space-y-1 py-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-medium">{wo.woNumber}</div>
                  <div className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs font-semibold">
                    {machineCount} machine{machineCount === 1 ? '' : 's'}
                  </div>
                </div>
                <div className="text-xs text-muted-foreground font-mono">
                  {wo.startDate} – {wo.endDate}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
